
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Avatar, Button, Layout, Menu } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, matchPath, useLocation, useMatches, useRouteLoaderData } from 'react-router-dom';
import { getUserDetails } from '../../api/user';
import { Server } from '../../library/constants/server.js';
import { getAvatarColor, getInitials } from '../../library/utilities/common.js';
import { useApp } from '../../providers/app/app-context.js';
import './sidebar.scss';
import styles from './sider.module.scss';

const { Sider } = Layout;

// Some detail routes don't nest under their logical section's menu URL (e.g.
// /sent-combi-deals/:id belongs under Order Manager > "Sent Combi Deals List", whose
// menu URL is /order-manager/sent-combi-deals — there's no path-prefix relationship
// between the two). Such routes declare an explicit `menuUrl` pointing at the section's
// menu URL so the sidebar can still resolve and highlight the right item.
const findMenuUrlOverride = (items, pathname) => {
  if (!items) return null

  for (const item of items) {
    if (item.url && item.menuUrl && matchPath({ path: item.url, end: false }, pathname)) {
      return item.menuUrl
    }
    const found = findMenuUrlOverride(item.children, pathname)
    if (found) return found
  }

  return null
}

// Resolves the current pathname (e.g. a detail page like /vendors/123) back to the
// closest visible menu item (e.g. the "Vendors" list with key /vendors?index), so the
// sidebar still highlights a leaf item when navigating away from a list view. AntD's
// Menu only applies "selected" styling to leaf Menu.Items, not SubMenu headers, so
// matching only the resolved route pathnames (as useMatches does) leaves nothing highlighted.
const findActiveMenuKey = (items, pathname, parent = '', ancestors = []) => {
  if (!items) return null

  let best = null

  for (const item of items.filter((item) => item.showOnMenu)) {
    const { url, children = null } = item
    const relativeUrl = url?.length ? url : parent
    const key = (!url && relativeUrl === parent) ? `${relativeUrl}?index` : relativeUrl
    const match = matchPath({ path: relativeUrl, end: false }, pathname)

    if (match && (!best || match.pathnameBase.length > best.matchLength)) {
      best = { key, ancestors, matchLength: match.pathnameBase.length }
    }

    const childMatch = findActiveMenuKey(children, pathname, relativeUrl, [...ancestors, relativeUrl])
    if (childMatch && (!best || childMatch.matchLength >= best.matchLength)) {
      best = childMatch
    }
  }

  return best
}

const getMenuItems = (items, parent = '') => {
  if (!items || items.filter((item) => item.showOnMenu).length == 0) return undefined

  return items.filter((item) => item.showOnMenu).map((item) => {
    const { url, nolink = false, label, icon = undefined, children = null } = item
    const relativeUrl = url?.length ? url : parent
    return {
      label: nolink ? label : <NavLink to={relativeUrl} className='nav-text' title={label} end viewTransition>{label}</NavLink>,
      key: (!url && relativeUrl === parent) ? `${relativeUrl}?index` : relativeUrl,
      icon: icon,
      children: getMenuItems(children, relativeUrl),
      id: url + parent,
    }
  })
}

export default function Sidebar(props) {
  const { routes } = props;
  const { theme, sidebarCollapsed: collapsed, actions } = useApp()
  const location = useLocation();
  const { user: rootUser } = useRouteLoaderData('Root')
  const [user, setUser] = useState(rootUser);

  useEffect(() => {
    setUser(rootUser);
  }, [rootUser]);

  useEffect(() => {
    const handleProfileUpdate = (e) => {
      if (e.detail) {
        setUser((prev) => ({ ...prev, ...e.detail }));
      }
    };
    window.addEventListener('user-profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('user-profile-updated', handleProfileUpdate);
  }, []);

  // Fetch latest profile from server if not already in session (e.g. after upload)
  useEffect(() => {
    if (user?.id && !user?.profile) {
      getUserDetails(user.id)
        .then((freshUser) => {
          if (freshUser?.profile) {
            setUser((prev) => ({ ...prev, profile: freshUser.profile }));
            const stored = JSON.parse(localStorage.getItem('USER') || 'null');
            if (stored && String(stored.id) === String(user.id)) {
              stored.profile = freshUser.profile;
              localStorage.setItem('USER', JSON.stringify(stored));
            }
          }
        })
        .catch(() => {});
    }
  }, [user?.id]);
  const menuItems = useMemo(() => getMenuItems(routes), [routes])
  const matches = useMatches()
  const pathnames = (matches?.map(({ pathname }) => pathname) || []).filter(Boolean);
  const currentPathWithIndex = `${location?.pathname || ''}?index`;
  const activeMenuItem = useMemo(() => {
    const menuUrlOverride = findMenuUrlOverride(routes, location.pathname)
    return findActiveMenuKey(routes, menuUrlOverride || location.pathname)
  }, [routes, location.pathname])
  const [selectedKeys, setSelectedKeys] = useState([...pathnames, currentPathWithIndex, ...(activeMenuItem ? [activeMenuItem.key] : [])])
  const [openKeys, setOpenKeys] = useState([...pathnames, ...(activeMenuItem?.ancestors || [])])
  const avatarSize = collapsed ? 44 : 80;

  useEffect(() => {
    setSelectedKeys([...pathnames, currentPathWithIndex, ...(activeMenuItem ? [activeMenuItem.key] : [])])
    setOpenKeys([...pathnames, ...(activeMenuItem?.ancestors || [])])
  }, [location])

  const toggleCollapsed = () => {
    actions.toggleSidebarCollapsed();
  };
  return (
    <Sider width={250} theme={theme} trigger={null} className={styles.sider} style={{ position: 'sticky', 'top': 0, zIndex: 9, height: '100vh' }} collapsible collapsed={collapsed}>
      {!collapsed ? <div className="logo-wraper"></div> : <div className="small-logo-wraper"></div>}
      <Button
        onClick={toggleCollapsed}
        style={{
          position: 'absolute',
          right: '0',
          top: '200px',
          transform: 'translateX(50%)',
          zIndex: '1',
        }}
        size={30}
        icon={collapsed ? <RightOutlined /> : < LeftOutlined />}
        shape='circle'
      />
      <div className={`user-info ${collapsed ? 'user-info-collapsed' : ''}`}>
        <div className="text">
          <Avatar
            key={user?.profile || 'no-profile'}
            size={avatarSize}
            src={user?.profile ? `${Server.BaseUrl}/api/v1/user/profile/${user.profile}` : undefined}
            style={{
              backgroundColor: getAvatarColor(user?.name),
              color: '#ffffff',
              fontSize: collapsed ? '16px' : '26px',
              fontWeight: 600,
            }}
          >
            {getInitials(user?.name, user?.email)}
          </Avatar>
          <h4><b>{user.name}</b></h4>
          <p>{user.role}</p>
        </div>
      </div>

      <Menu
        // key={location.pathname}
        // defaultSelectedKeys={[...pathnames, ...pathnames.map(path => `${path}?index`), currentPathWithIndex]}
        selectedKeys={selectedKeys}
        defaultOpenKeys={openKeys}
        mode="inline"
        theme={theme}
        className={styles.menu}
        inlineCollapsed={collapsed}
        items={menuItems}
      />
      {/* <Menu theme={theme} mode="inline" items={getMenuItems(routes)} defaultSelectedKeys={[location]} /> */}


      <a href={process.env.REACT_APP_BUILD_LOGS} target='blank' className={`build-version ${collapsed ? 'build-version-collapsed' : ''}`}> <p style={{
        fontSize: '11px',
        fontWeight: '400',
        lineHeight: "11px",
        letterSpacing: '0.03em',
        textAlign: 'center',
        color: '#8F8F8F',
        margin: 0
      }
      }>{collapsed ? process.env.REACT_APP_CURRENT_BUILD : `Version ${process.env.REACT_APP_CURRENT_BUILD}`}</p></a>
    </Sider>
  )
}


Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
}
